import { Button, Space } from "antd";
import type { InstallPromotionCommissionVo } from "@zhihaoscm/entity";
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router";
import { BaseItem as ZhBaseItem, DetailHeader as ZhDetailHeader, Grid as ZhGrid } from "zhihao-ui-react";

import request from "@/api";
import { DEFAULT_VALUE } from "@/config";
import { INSTALL_PROMOTION_COMMISSION_STATE } from "@/meta/expenseManage/installPromotionCommission";
import { WAYBILL_STATE } from "@/meta/logistics/waybill";
import { Permission } from "@/views/components/Permission";
import { useUserStore } from "@/stores/react/authStore";

type CommissionDetail = Partial<InstallPromotionCommissionVo>;

export interface InstallPromotionCommissionDetailPageProps {
    commissionId?: number | string;
    loadCommission?: (id: number | string) => Promise<CommissionDetail | undefined>;
}

/** 复用 Vue 安装推广佣金详情接口。 */
export async function loadInstallPromotionCommissionDetail(id: number | string) {
    const response = await request.service.get<CommissionDetail>(`/install-promotion-commission/vo/${id}`);
    return response.data;
}

const formatDate = (value?: string) => (value ? value.replace("T", " ").slice(0, 16) : DEFAULT_VALUE);
const formatCustomer = (value?: { institutionName?: string; realName?: string; mobile?: string; code?: string }) =>
    value
        ? `${value.institutionName || value.realName || DEFAULT_VALUE} | ${value.mobile || DEFAULT_VALUE} | ${value.code || DEFAULT_VALUE}`
        : DEFAULT_VALUE;
const formatAmount = (value?: number) => (value == null ? DEFAULT_VALUE : `${value}元`);

/** 费用管理-安装推广佣金详情，覆盖 Vue 详情字段和结算入口。 */
export function InstallPromotionCommissionDetailPage({
    commissionId,
    loadCommission = loadInstallPromotionCommissionDetail
}: InstallPromotionCommissionDetailPageProps) {
    const params = useParams<{ id: string }>();
    const location = useLocation();
    const navigate = useNavigate();
    const banButton = useUserStore(state => state.banButton);
    // 兼容直接挂载页面的 React 契约测试，并保留正式路由的 useParams 优先级。
    const resolvedId = commissionId ?? params.id ?? location.pathname.split("/").filter(Boolean).pop();
    const [detail, setDetail] = useState<CommissionDetail>();

    useEffect(() => {
        // 对齐 Vue 详情页的 `if (id)` 约束，缺少编号时不触发无效的 /vo/0 请求。
        if (!resolvedId) return;

        let disposed = false;
        void loadCommission(resolvedId).then(result => {
            if (!disposed) setDetail(result);
        });
        return () => {
            disposed = true;
        };
    }, [loadCommission, resolvedId]);

    const commission = detail?.commission;
    const settled = commission?.state === INSTALL_PROMOTION_COMMISSION_STATE.SETTLED.code;
    const canSettle = !settled && detail?.orderShipState === WAYBILL_STATE.COMPLETED.code;

    return (
        <section
            aria-label="install-promotion-commission-detail-page"
            className="detail-page-transparent flex min-h-full flex-col gap-4"
        >
            <div className="section-card">
                <ZhDetailHeader
                    leftInfos={[{ label: "编号", value: commission?.id || DEFAULT_VALUE }]}
                    rightInfos={[{ label: "创建时间", value: formatDate(commission?.createdTime) }]}
                    stateDesc={INSTALL_PROMOTION_COMMISSION_STATE.getIntroByCode(commission?.state) || DEFAULT_VALUE}
                    title="安装推广佣金详情"
                />
            </div>
            <div className="section-card">
                <div className="section-title mb-5">相关信息</div>
                <ZhGrid cols={4} gap={[10, 20]}>
                    <ZhBaseItem label="船舶">
                        {commission?.shipId ? (
                            // 沿用 Vue 船舶详情命名路由，确保佣金详情关联船舶可正常打开。
                            <Link
                                to={`/logistics/shipManage/shipInfo/shipInfoDetail/${commission.shipId}`}
                            >{`${commission.shipCnname || DEFAULT_VALUE}/${commission.shipName || DEFAULT_VALUE}`}</Link>
                        ) : (
                            DEFAULT_VALUE
                        )}
                    </ZhBaseItem>
                    <ZhBaseItem label="船运单">
                        {commission?.orderShipId ? (
                            // 对齐 Vue WaybillManagementInfo 命名路由在 React 路由树中的实际路径。
                            <Link to={`/logistics/shippingNeed/waybillManagementInfo/${commission.orderShipId}`}>
                                {commission.orderShipId}
                            </Link>
                        ) : (
                            DEFAULT_VALUE
                        )}
                    </ZhBaseItem>
                    <ZhBaseItem label="船务信息服务费">
                        {commission?.shipInfoServiceFeeId ? (
                            <Link
                                to={`/expenseManagement/shippingServiceFee/detail/${commission.shipInfoServiceFeeId}`}
                            >
                                {commission.shipInfoServiceFeeId}
                            </Link>
                        ) : (
                            DEFAULT_VALUE
                        )}
                    </ZhBaseItem>
                </ZhGrid>
            </div>
            <div className="section-card">
                <div className="section-title mb-5">结算信息</div>
                <ZhGrid cols={4} gap={[10, 20]}>
                    <ZhBaseItem label="安装推广记录">
                        {commission?.installPromotionRecordId ? (
                            <Link to={`/logistics/installPromotionRecord/info/${commission.installPromotionRecordId}`}>
                                {commission.installPromotionRecordCode || DEFAULT_VALUE}
                            </Link>
                        ) : (
                            DEFAULT_VALUE
                        )}
                    </ZhBaseItem>
                    <ZhBaseItem label="推介人账号" value={formatCustomer(commission?.promoterInfo)} />
                </ZhGrid>
                {settled && detail?.platformBankAccount ? (
                    <ZhGrid className="mt-5" cols={4} gap={[10, 20]}>
                        <ZhBaseItem label="开户行名称" value={detail.platformBankAccount.name || DEFAULT_VALUE} />
                        <ZhBaseItem label="银行账户" value={detail.platformBankAccount.account || DEFAULT_VALUE} />
                        <ZhBaseItem label="开户行" value={detail.platformBankAccount.openingBank || DEFAULT_VALUE} />
                    </ZhGrid>
                ) : null}
                <ZhGrid className="mt-5" cols={4} gap={[10, 20]}>
                    <ZhBaseItem label="金额" value={formatAmount(commission?.commissionAmount)} />
                    {settled ? (
                        <ZhBaseItem label="结算人" value={commission?.settlementUserName || DEFAULT_VALUE} />
                    ) : null}
                    <ZhBaseItem label="时间" value={settled ? formatDate(commission?.settlementTime) : DEFAULT_VALUE} />
                </ZhGrid>
            </div>
            <Space className="operation-fixed-btn" size={0}>
                {canSettle ? (
                    <Permission anyOf={["ROLE_DEVICE_BONUS_W"]}>
                        <Button
                            aria-label="结算"
                            disabled={banButton}
                            type="primary"
                            onClick={() => navigate(`/expenseManagement/installPromotion/settle/${commission?.id}`)}
                        >
                            结算
                        </Button>
                    </Permission>
                ) : null}
                <Button type="primary" onClick={() => navigate(-1)}>
                    返回
                </Button>
            </Space>
        </section>
    );
}
